import React from "react";
import { Card, CircularProgress, Tooltip, Button } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { PrimaryText } from "components/Common/PrimaryText";
import InfoTooltip from "./InfoTooltip";
import { useDisplay } from "context/DisplayContext";

const EngagementFunnel = ({
	metrics,
	loading,
	error,
	filter,
	engagementLoading,
}) => {
	const { isMobile } = useDisplay();

	// Use individual loading state if provided, otherwise use general loading
	const isEngagementLoading =
		engagementLoading !== undefined ? engagementLoading : loading;
	if (isEngagementLoading) {
		return (
			<Card
				style={{
					padding: 20,
					textAlign: "center",
					height: isMobile ? "auto" : 400,
				}}
			>
				<CircularProgress />
				<PrimaryText>Loading engagement data...</PrimaryText>
			</Card>
		);
	}

	if (error) {
		return (
			<Card
				style={{
					padding: 20,
					textAlign: "center",
					height: isMobile ? "auto" : 400,
				}}
			>
				<PrimaryText color="error">
					Error loading engagement data: {error}
				</PrimaryText>
			</Card>
		);
	}

	const engagementMetrics = metrics?.engagementMetrics;

	// Show specific message if engagement metrics failed to load
	if (!engagementMetrics) {
		return (
			<Card
				style={{
					padding: 20,
					textAlign: "center",
					height: isMobile ? "auto" : 400,
				}}
			>
				<PrimaryText style={{ color: "#666" }}>
					Engagement metrics unavailable
				</PrimaryText>
				<PrimaryText fontSize={12} style={{ color: "#999", marginTop: 8 }}>
					Try refreshing or check back later
				</PrimaryText>
			</Card>
		);
	}

	// Get metrics based on filter - now engagementMetrics is already filtered
	const getMetricValue = (metric) => {
		return engagementMetrics[metric] || 0;
	};

	// Funnel stages data
	const stages = [
		{
			name: "Signup",
			value: 100,
			description: "All users who created an account",
		},
		{
			name: "Upload Photos",
			value: getMetricValue("photoUploadRate"),
			description: "Users who uploaded at least one photo",
		},
		{
			name: "Verified",
			value: getMetricValue("verificationRate"),
			description: "Users who completed profile verification",
		},
		{
			name: "First Match",
			value: getMetricValue("firstMatchRate") * 100,
			description: "Users who received their first match",
		},
		{
			name: "First Message",
			value: getMetricValue("firstMessageRate") * 100,
			description: "Users who sent their first message",
		},
	];

	return (
		<Card
			style={{
				padding: isMobile ? 12 : 20,
				height: isMobile ? "auto" : 400,
				overflow: "auto",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					marginBottom: 20,
				}}
			>
				<PrimaryText fontSize={isMobile ? 16 : 20} fontWeight={700}>
					Engagement Metrics
				</PrimaryText>
				<InfoTooltip
					title="User engagement analytics"
					content="Shows how users engage with the app based on their signup date range. Metrics are calculated in real-time."
				/>
			</div>

			{/* Metrics Grid */}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
					gap: isMobile ? 8 : 16,
					marginBottom: 20,
				}}
			>
				{/* User Actions */}
				<div
					style={{ padding: 12, backgroundColor: "#f8f9fa", borderRadius: 8 }}
				>
					<div
						style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
					>
						<PrimaryText
							fontSize={14}
							fontWeight={600}
							style={{ color: "#1976d2" }}
						>
							User Actions
						</PrimaryText>
						<Tooltip
							title={
								<div style={{ padding: 4 }}>
									<div style={{ fontSize: 12, fontWeight: 600 }}>
										User Engagement Metrics
									</div>
									<div style={{ fontSize: 11, marginTop: 2 }}>
										Measures how actively users are engaging with the platform
										through messaging and matching.
									</div>
								</div>
							}
							arrow
							placement="top"
						>
							<InfoIcon
								style={{
									fontSize: 14,
									color: "#999",
									marginLeft: 6,
									cursor: "help",
								}}
							/>
						</Tooltip>
					</div>
					<div style={{ spaceY: 4 }}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: 4,
							}}
						>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Avg Messages:
							</PrimaryText>
							<PrimaryText fontSize={12} fontWeight={500}>
								{engagementMetrics.avgMessagesPerUser?.toFixed(1) || "0"}
							</PrimaryText>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: 4,
							}}
						>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Avg Matches:
							</PrimaryText>
							<PrimaryText fontSize={12} fontWeight={500}>
								{engagementMetrics.avgMatchesPerUser?.toFixed(1) || "0"}
							</PrimaryText>
						</div>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Time to First Match:
							</PrimaryText>
							<PrimaryText fontSize={12} fontWeight={500}>
								{engagementMetrics.firstMatchTime?.toFixed(1) || "0"} days
							</PrimaryText>
						</div>
					</div>
				</div>

				{/* Profile Quality */}
				<div
					style={{ padding: 12, backgroundColor: "#f8f9fa", borderRadius: 8 }}
				>
					<div
						style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
					>
						<PrimaryText
							fontSize={14}
							fontWeight={600}
							style={{ color: "#1976d2" }}
						>
							Profile Quality
						</PrimaryText>
						<Tooltip
							title={
								<div style={{ padding: 4 }}>
									<div style={{ fontSize: 12, fontWeight: 600 }}>
										Profile Completion Metrics
									</div>
									<div style={{ fontSize: 11, marginTop: 2 }}>
										Indicates how complete and authentic user profiles are,
										which correlates with higher engagement.
									</div>
								</div>
							}
							arrow
							placement="top"
						>
							<InfoIcon
								style={{
									fontSize: 14,
									color: "#999",
									marginLeft: 6,
									cursor: "help",
								}}
							/>
						</Tooltip>
					</div>
					<div style={{ spaceY: 4 }}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: 4,
							}}
						>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Verified Users:
							</PrimaryText>
							<PrimaryText fontSize={12} fontWeight={500}>
								{engagementMetrics.verificationRate?.toFixed(1) || "0"}%
							</PrimaryText>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: 4,
							}}
						>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Photo Upload Rate:
							</PrimaryText>
							<PrimaryText fontSize={12} fontWeight={500}>
								{engagementMetrics.photoUploadRate?.toFixed(1) || "0"}%
							</PrimaryText>
						</div>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Avg Photos:
							</PrimaryText>
							<PrimaryText fontSize={12} fontWeight={500}>
								{engagementMetrics.avgPhotoCount?.toFixed(1) || "0"}
							</PrimaryText>
						</div>
					</div>
				</div>

				{/* Interaction Rates */}
				<div
					style={{ padding: 12, backgroundColor: "#f8f9fa", borderRadius: 8 }}
				>
					<div
						style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
					>
						<PrimaryText
							fontSize={14}
							fontWeight={600}
							style={{ color: "#1976d2" }}
						>
							Interaction Rates
						</PrimaryText>
						<Tooltip
							title={
								<div style={{ padding: 4 }}>
									<div style={{ fontSize: 12, fontWeight: 600 }}>
										Conversation Quality Metrics
									</div>
									<div style={{ fontSize: 11, marginTop: 2 }}>
										Measures how effectively matches lead to conversations and
										how many users disengage.
									</div>
								</div>
							}
							arrow
							placement="top"
						>
							<InfoIcon
								style={{
									fontSize: 14,
									color: "#999",
									marginLeft: 6,
									cursor: "help",
								}}
							/>
						</Tooltip>
					</div>
					<div style={{ spaceY: 4 }}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: 4,
							}}
						>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Conversation Start:
							</PrimaryText>
							<PrimaryText
								fontSize={12}
								fontWeight={500}
								style={{ color: "#4caf50" }}
							>
								{engagementMetrics.conversationStartRate?.toFixed(1) || "0"}%
							</PrimaryText>
						</div>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Ghosting Rate:
							</PrimaryText>
							<PrimaryText
								fontSize={12}
								fontWeight={500}
								style={{ color: "#f44336" }}
							>
								{engagementMetrics.ghostingRate?.toFixed(1) || "0"}%
							</PrimaryText>
						</div>
					</div>
				</div>

				{/* User Base */}
				<div
					style={{ padding: 12, backgroundColor: "#f8f9fa", borderRadius: 8 }}
				>
					<div
						style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
					>
						<PrimaryText
							fontSize={14}
							fontWeight={600}
							style={{ color: "#1976d2" }}
						>
							User Base
						</PrimaryText>
						<Tooltip
							title={
								<div style={{ padding: 4 }}>
									<div style={{ fontSize: 12, fontWeight: 600 }}>
										User Growth Metrics
									</div>
									<div style={{ fontSize: 11, marginTop: 2 }}>
										Shows the size and activity level of your user base over
										time.
									</div>
								</div>
							}
							arrow
							placement="top"
						>
							<InfoIcon
								style={{
									fontSize: 14,
									color: "#999",
									marginLeft: 6,
									cursor: "help",
								}}
							/>
						</Tooltip>
					</div>
					<div style={{ spaceY: 4 }}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: 4,
								alignItems: "center",
							}}
						>
							<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
								<PrimaryText fontSize={12} style={{ color: "#666" }}>
									New Users (period):
								</PrimaryText>
								<Tooltip
									title={
										<div style={{ padding: 4 }}>
											<div style={{ fontSize: 11, fontWeight: 600 }}>
												New Users in Period
											</div>
											<div style={{ fontSize: 10, marginTop: 2 }}>
												Users who signed up within the selected time period. Not
												the all-time total.
											</div>
										</div>
									}
									arrow
									placement="top"
								>
									<InfoIcon
										style={{
											fontSize: 12,
											color: "#999",
											cursor: "help",
										}}
									/>
								</Tooltip>
							</div>
							<PrimaryText fontSize={12} fontWeight={500}>
								{engagementMetrics.totalUsers?.toLocaleString() || "0"}
							</PrimaryText>
						</div>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<PrimaryText fontSize={12} style={{ color: "#666" }}>
								Active (MAU):
							</PrimaryText>
							<PrimaryText fontSize={12} fontWeight={500}>
								{engagementMetrics.mau?.toLocaleString() || "0"}
							</PrimaryText>
						</div>
					</div>
				</div>
			</div>

			{/* Funnel Visualization */}
			<div style={{ borderTop: "1px solid #e0e0e0", paddingTop: 16 }}>
				<div
					style={{ display: "flex", alignItems: "center", marginBottom: 12 }}
				>
					<PrimaryText fontSize={14} fontWeight={600}>
						Conversion Funnel
					</PrimaryText>
					<Tooltip
						title={
							<div style={{ padding: 4 }}>
								<div style={{ fontSize: 12, fontWeight: 600 }}>
									User Journey Funnel
								</div>
								<div style={{ fontSize: 11, marginTop: 2 }}>
									Shows the percentage of users who complete each step of the
									onboarding and engagement process.
								</div>
							</div>
						}
						arrow
						placement="top"
					>
						<InfoIcon
							style={{
								fontSize: 14,
								color: "#999",
								marginLeft: 6,
								cursor: "help",
							}}
						/>
					</Tooltip>
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 8,
						flexWrap: isMobile ? "wrap" : "nowrap",
					}}
				>
					{stages.map((stage, index) => {
						const width = stage.value ? Math.max(stage.value, 5) : 0;
						const isLast = index === stages.length - 1;

						// Get insight for each stage
						let insight = "";
						switch (stage.name) {
							case "Signup":
								insight = "All users who created an account on the platform";
								break;
							case "Upload Photos":
								insight =
									width >= 80
										? "Excellent photo upload rate! Users are engaging with profile creation."
										: width >= 60
											? "Good photo upload rate. Consider optimizing the photo upload experience."
											: "Low photo upload rate. Users may be having difficulty or seeing little value in adding photos.";
								break;
							case "Verified":
								insight =
									width >= 70
										? "Great verification rate! Users trust your platform."
										: width >= 50
											? "Moderate verification rate. Consider streamlining the verification process."
											: "Low verification rate. Users may be skeptical about providing personal information.";
								break;
							case "First Match":
								insight =
									width >= 60
										? "Strong matching algorithm! Users are finding compatible matches."
										: width >= 40
											? "Moderate match rate. Review your matching criteria and algorithms."
											: "Low match rate. Users may not be finding value in the matching system.";
								break;
							case "First Message":
								insight =
									width >= 70
										? "Excellent conversation initiation! Users are comfortable reaching out."
										: width >= 50
											? "Good message rate. Consider providing conversation starters or icebreakers."
											: "Low message rate. Users may feel hesitant to initiate conversations.";
								break;
							default:
								insight = stage.description;
						}

						return (
							<React.Fragment key={index}>
								<Tooltip
									title={
										<div style={{ padding: 4 }}>
											<div style={{ fontSize: 12, fontWeight: 600 }}>
												{stage.name}
											</div>
											<div style={{ fontSize: 11, marginTop: 2 }}>
												{insight}
											</div>
										</div>
									}
									arrow
									placement="top"
								>
									<div
										style={{
											flex: isMobile ? "1 1 calc(50% - 8px)" : 1,
											textAlign: "center",
											cursor: "help",
										}}
									>
										<div
											style={{
												height: 24,
												backgroundColor:
													width > 50
														? "#4caf50"
														: width > 25
															? "#ff9800"
															: "#f44336",
												borderRadius: 4,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												marginBottom: 4,
												minWidth: "40px",
											}}
										>
											<PrimaryText fontSize={10} style={{ color: "white" }}>
												{width.toFixed(0)}%
											</PrimaryText>
										</div>
										<PrimaryText fontSize={10} style={{ color: "#666" }}>
											{stage.name}
										</PrimaryText>
									</div>
								</Tooltip>
								{!isLast && !isMobile && (
									<div style={{ color: "#999", fontSize: 16 }}>→</div>
								)}
							</React.Fragment>
						);
					})}
				</div>
			</div>

			{/* Data Range Info */}
			<div
				style={{
					marginTop: 16,
					padding: 8,
					backgroundColor: "#f5f5f5",
					borderRadius: 4,
				}}
			>
				<div style={{ display: "flex", alignItems: "center" }}>
					<PrimaryText fontSize={11} style={{ color: "#666" }}>
						Data for users who signed up between{" "}
						{engagementMetrics.dataRange?.start || "N/A"} and{" "}
						{engagementMetrics.dataRange?.end || "N/A"} (
						{engagementMetrics.dataRange?.days || 0} days)
					</PrimaryText>
					<Tooltip
						title={
							<div style={{ padding: 4 }}>
								<div style={{ fontSize: 12, fontWeight: 600 }}>
									Data Range Information
								</div>
								<div style={{ fontSize: 11, marginTop: 2 }}>
									Metrics are calculated for users who signed up during this
									period. Use the date filter above to change the analysis
									period.
								</div>
							</div>
						}
						arrow
						placement="top"
					>
						<InfoIcon
							style={{
								fontSize: 12,
								color: "#999",
								marginLeft: 6,
								cursor: "help",
							}}
						/>
					</Tooltip>
				</div>
			</div>
		</Card>
	);
};

export default EngagementFunnel;
